// Agency Console theme picker — shared by Settings → Appearance and the onboarding
// "Choose your look" step. Pure-CSS miniatures (no screenshots to go stale); each swatch
// renders with the agency's own accent because the vars resolve at paint time.
import { THEMES, THEME_IDS, type ThemeDefinition, type ThemeId } from '../themes';

function Mini({ theme }: { theme: ThemeDefinition }) {
  const v = theme.cssVars;
  return (
    <div
      className="flex h-24 overflow-hidden rounded-lg border border-black/5"
      style={{ background: v['--th-canvas'] }}
    >
      {/* Sidebar rail in the theme's own nav tone (HQ = dark gradient) with tiny nav stubs */}
      <div
        className="flex w-7 shrink-0 flex-col items-center gap-1 py-1.5"
        style={{ background: v['--th-nav-bg'], borderRight: `1px solid ${v['--th-nav-border']}` }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="block h-1.5 w-4 rounded-full"
            style={{ background: i === 0 ? v['--th-nav-active-bg'] : v['--th-nav-hover-bg'] }}
          />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-2">
        {[
          { w: '90%', h: 16 },
          { w: '65%', h: 12, accent: true },
          { w: '75%', h: 12 },
        ].map((bar, i) => (
          <div
            key={i}
            style={{
              width: bar.w,
              height: bar.h,
              background: v['--th-surface'],
              borderRadius: 5,
              boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              borderLeft: bar.accent ? '3px solid var(--ss-accent, #1a73e8)' : undefined,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function ThemePreviewCard({
  theme,
  selected,
  onSelect,
}: {
  theme: ThemeDefinition;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full rounded-xl border-2 p-3 text-left transition ${
        selected ? 'border-brand ring-2 ring-brand/20' : 'border-th-border hover:border-muted'
      }`}
    >
      <Mini theme={theme} />
      <div className="mt-2.5 text-sm font-semibold text-ink">{theme.meta.name}</div>
      <div className="mt-0.5 text-xs text-muted">{theme.meta.description}</div>
    </button>
  );
}

/** The three cards in a responsive row. */
export function ThemePicker({
  value,
  onSelect,
}: {
  value: ThemeId;
  onSelect: (id: ThemeId) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {THEME_IDS.map((id) => (
        <ThemePreviewCard key={id} theme={THEMES[id]} selected={id === value} onSelect={() => onSelect(id)} />
      ))}
    </div>
  );
}
